/**
 * @beam/api-types — Help & Guidance contract (WO-H1).
 *
 * The SINGLE shared shape for in-app help, consumed identically by both clients
 * (beam-mobile native + its Expo-web target). Help content is DATA, not code:
 * the app ships a default bundle (so help works offline / on first launch) and the
 * backend MAY return per-tenant overrides merged over those defaults.
 *
 * Invariants honored:
 *  - No secrets, no PII in help content (it is publishable, cacheable text).
 *  - Trade-agnostic: entries are keyed by stable screen/state IDs, never by a trade
 *    or client name, so the same bundle redeploys to any contractor tenant.
 *
 * Barrel: add `export * from './help.js';` to src/index.ts (see MERGE-GUIDE-H1.md).
 */
import { z } from 'zod';
/** BCP-47-ish locale tag. English-only for H1; the field is reserved so multi-locale
 *  authoring needs no schema change later. */
export const HelpLocale = z.string().min(2).default('en');
/**
 * One contextual help entry — what the "?" on a screen explains.
 * `screenKey` addresses the screen; optional `stateKey` lets one screen carry
 * different help per state (e.g. 'empty' vs 'has-items'). The most specific match wins.
 */
export const HelpEntry = z.object({
    id: z.string().min(1),
    screenKey: z.string().min(1),
    stateKey: z.string().min(1).optional(),
    title: z.string().min(1),
    /** Plain-language body. Markdown-lite (paragraphs + `- ` bullets). No HTML. */
    body: z.string().min(1),
    /** Other entry ids to surface as "related" links. */
    relatedIds: z.array(z.string().min(1)).optional(),
    locale: HelpLocale.optional(),
});
/** One step of a first-run coachmark flow. `anchorKey` is an optional UI anchor id
 *  a client may use to position the step; clients without the anchor fall back to a
 *  centered card. */
export const Coachmark = z.object({
    id: z.string().min(1),
    flowKey: z.string().min(1),
    stepOrder: z.number().int().nonnegative(),
    anchorKey: z.string().min(1).optional(),
    title: z.string().min(1),
    body: z.string().min(1),
    locale: HelpLocale.optional(),
});
/** A step in a Help Center "How do I…" guide. `screenKey` optionally deep-links the
 *  step to a screen; `imageUrl` is reserved for the H1-P2 screenshot generator. */
export const HelpGuideStep = z.object({
    title: z.string().min(1),
    body: z.string().min(1),
    screenKey: z.string().min(1).optional(),
    imageUrl: z.string().url().optional(),
});
/** A Help Center reference guide. */
export const HelpGuide = z.object({
    id: z.string().min(1),
    title: z.string().min(1),
    /** Short one-line summary for the Help Center list row. */
    summary: z.string().min(1).optional(),
    steps: z.array(HelpGuideStep).min(1),
    locale: HelpLocale.optional(),
});
/**
 * The full content bundle. Shipped as a default in each client and/or returned by
 * GET /api/help-content. `version` lets a client cache and compare bundles.
 */
export const HelpContentBundle = z.object({
    version: z.string().min(1),
    locale: HelpLocale,
    entries: z.array(HelpEntry),
    coachmarks: z.array(Coachmark),
    guides: z.array(HelpGuide),
});
/**
 * GET /api/help-content?locale=en  → tenant-merged bundle (defaults ⊕ org overrides).
 * Public-ish but org-scoped when authenticated; never returns another tenant's content.
 * Response IS a bundle (alias kept distinct for endpoint clarity / future envelope).
 */
export const HelpContentResponse = HelpContentBundle;
